import React from 'react'
import { Bleed, FlowGrid, H1, LayoutBand, Paragraph, Row, Separator, usePageWidth, WhiteTheme } from '@fs/zion-ui'
import PropTypes from 'prop-types'

import ImageSlideshow from '../../ImageSlideshow'
import LanguageSelect from '../components/LanguageSelect'
import SaveToHomeButton from '../components/SaveToHomeOverlay'

export default function LeadSectionA({
  heading,
  subheading,
  supplementary,
  actionBar,
  color,
  mobileBgImage,
  mobileBgImagePosition,
  mobileBgImageSize,
  bgImage,
  bgImagePosition,
  bgImageSize,
  slideshowBg,
  images,
  showSlideshowOnMobile,
  whiteTheme,
  hideSave,
  languagePickerOptions,
  imageFullHeight = false,
}) {
  const atWidth = usePageWidth()
  const isMobile = atWidth({ default: true, xl: false })
  const marginX = atWidth({ default: 'xl', md: 'sm' })

  const Wrapper = whiteTheme ? WhiteTheme : React.Fragment

  const layoutBandStyle = {
    backgroundPosition: isMobile ? mobileBgImagePosition : bgImagePosition,
    backgroundSize: isMobile ? mobileBgImageSize || 'cover' : bgImageSize || 'contain',
    overflow: 'hidden', // Prevent image slideshow overflowing from layout band, but allowing for bleed within
  }

  return (
    <LayoutBand
      marginY={isMobile ? 'xl' : 'xxl'}
      marginX={marginX}
      color={color}
      image={isMobile ? mobileBgImage : bgImage || undefined}
      style={layoutBandStyle}
    >
      <Wrapper>
        <FlowGrid columnCount={isMobile ? 1 : 2}>
          <>
            {languagePickerOptions && (
              <>
                <LanguageSelect options={languagePickerOptions} />
                <Separator size="xs" />
              </>
            )}
            <H1 supplementary={supplementary} size={supplementary ? 'H1' : 'H2'} centered={isMobile} balanced>
              {heading}
            </H1>
            <Separator size="xs" />
            <div style={{ maxWidth: '400px', margin: isMobile ? '0 auto' : undefined }}>
              <Paragraph size="sm" centered={isMobile}>
                {subheading}
              </Paragraph>
            </div>
            <Separator size="md" />
            {actionBar}
            {isMobile && !hideSave && (
              <>
                <Separator size="md" />
                <Row alignX="center">
                  <SaveToHomeButton />
                </Row>
              </>
            )}
          </>
          {images && (!isMobile || showSlideshowOnMobile) && (
            <Bleed bottom sides top={!isMobile}>
              <div style={{ minHeight: '350px', height: '100%' }}>
                <ImageSlideshow
                  images={images}
                  maxHeight={650}
                  bgImage={slideshowBg || (isMobile ? mobileBgImage : bgImage)}
                  bgImagePosition={
                    isMobile ? mobileBgImagePosition || 'center bottom' : bgImagePosition || 'center bottom'
                  }
                  fullHeight={imageFullHeight}
                />
              </div>
            </Bleed>
          )}
        </FlowGrid>
      </Wrapper>
    </LayoutBand>
  )
}

LeadSectionA.propTypes = {
  heading: PropTypes.node.isRequired,
  subheading: PropTypes.node.isRequired,
  supplementary: PropTypes.bool,
  actionBar: PropTypes.node,
  color: PropTypes.string,
  mobileBgImage: PropTypes.string,
  mobileBgImagePosition: PropTypes.string,
  mobileBgImageSize: PropTypes.string,
  bgImage: PropTypes.string,
  bgImagePosition: PropTypes.string,
  bgImageSize: PropTypes.string,
  slideshowBg: PropTypes.string,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
    })
  ),
  showSlideshowOnMobile: PropTypes.bool,
  whiteTheme: PropTypes.bool,
  hideSave: PropTypes.bool,
  languagePickerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
}
